import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, FormControlLabel, Checkbox, Slider, Typography, Box, Paper, Divider } from '@mui/material';
import Button from '../../shared/Button';
import Tooltips from '../Tooltips';
import useAPI from "../../utils/api";
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useSnackbar } from 'notistack';
import SiteLink from '../../shared/SiteLink';

const SpamShield = ({ site }) => {
  const API = useAPI();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const isFreePlan = site.plan?.free === true || site.plan?.free_forever === true;
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [settings, setSettings] = useState({
    spam_shield_enabled: site.spam_shield_enabled || false,
    spam_shield_threshold: site.spam_shield_threshold || 4,
    spam_shield_auto_remove: site.spam_shield_auto_remove !== false,
    spam_shield_verification_enabled: site.spam_shield_verification_enabled !== false,
  });

  useEffect(() => {
    const fetchStats = async () => {
      const [ok, data] = await API.spamShieldStats();
      if (ok) setStats(data);
    };
    fetchStats();
  }, []);

  const totalAttempts = stats.reduce((acc, curr) => acc + (curr.attempts || 0), 0);
  const totalBlocked = stats.reduce((acc, curr) => acc + curr.blocked, 0);
  const totalVerified = stats.reduce((acc, curr) => acc + curr.verified, 0);
  const totalDeliverable = stats.reduce((acc, curr) => acc + (curr.deliverable || 0), 0);
  const cleanRate = totalAttempts > 0 ? (((totalAttempts - totalBlocked) / totalAttempts) * 100).toFixed(1) : 100;

  const handleChange = (name) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const newSettings = { ...settings, [name]: value };

    // Auto-enable email verification when spam shield is first turned on
    if (name === 'spam_shield_enabled' && value === true && !site.spam_shield_enabled) {
      newSettings.spam_shield_verification_enabled = true;
    }

    setSettings(newSettings);
  };

  const handleSliderChange = (event, newValue) => {
    setSettings({ ...settings, spam_shield_threshold: newValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const [, updated] = await API.updateSite({ id: site.id, ...settings });
    if (updated) {
      site.update(updated);
      enqueueSnackbar('Spam Shield settings saved successfully', {
        variant: 'success',
        preventDuplicate: true,
      });
      try {
        const settingsChanged = [];
        if (settings.spam_shield_threshold !== site.spam_shield_threshold) settingsChanged.push('threshold');
        if (settings.spam_shield_auto_remove !== site.spam_shield_auto_remove) settingsChanged.push('auto_remove');
        if (settings.spam_shield_verification_enabled !== site.spam_shield_verification_enabled) settingsChanged.push('verification_enabled');
        if (settingsChanged.length > 0) {
          window.analytics?.track('Spam Shield Configured', {
            settings_changed: settingsChanged,
            threshold: settings.spam_shield_threshold,
            auto_remove: settings.spam_shield_auto_remove,
            verification_enabled: settings.spam_shield_verification_enabled,
            site_id: site.id,
          });
        }
      } catch (_) {}
    }
    setLoading(false);
  };

  if (isFreePlan) {
    return (
      <Box p={4} textAlign="center">
        <Paper elevation={0} sx={{ p: 6, borderRadius: 8, border: '1px solid #e2e8f0', maxWidth: 650, margin: '0 auto' }}>
          <Box mb={3} position="relative" display="inline-block">
            <SecurityIcon sx={{ fontSize: 80, color: '#ED5A29', opacity: 0.1 }} />
            <SecurityIcon sx={{ fontSize: 40, color: '#334155', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          </Box>
          
          <Typography variant="h4" fontWeight="800" gutterBottom>Unlock Spam Shield Protection</Typography>
          <Typography variant="body1" color="textSecondary" mb={4}>
            Stop 99.9% of bot submissions and verify every email lead in real-time.
          </Typography>
          
          <Box display="inline-block" textAlign="left" mb={4}>
            <Box display="flex" alignItems="center" gap={2} mb={1.5}>
              <VerifiedUserIcon sx={{ color: '#10b981' }} /> <Typography variant="body2" fontWeight="600">Real-time bot protection</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2} mb={1.5}>
              <VerifiedUserIcon sx={{ color: '#10b981' }} /> <Typography variant="body2" fontWeight="600">Automated email verification</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2} mb={1.5}>
              <VerifiedUserIcon sx={{ color: '#10b981' }} /> <Typography variant="body2" fontWeight="600">Improved lead quality and deliverability</Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <SiteLink to="/upgrade">
              <Button 
                color="secondary"
                label="Upgrade to Unlock"
                size="large"
                style={{ padding: '12px 40px' }}
              />
            </SiteLink>
            <Box mt={3} pt={2} sx={{ borderTop: '1px solid #f1f5f9' }}>

              <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block', lineHeight: 1.5 }}>
                * Spam Shield is an optional premium add-on available on Growth, Elite, and Premium plans.
              </Typography>
            </Box>
          </Box>


        </Paper>
      </Box>
    );
  }

  return (

    <Box id="spam-shield-settings" p={1}>
      <Box display="flex" alignItems="center" mb={3}>
        <SecurityIcon style={{ fontSize: 32, marginRight: 12, color: '#ED5A29' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Spam Shield Protection</Typography>
          <Typography variant="body2" color="textSecondary">
            Advanced protection to keep your leads clean and your costs low.
          </Typography>
        </Box>
      </Box>

      {/* Protection Summary */}
      <Grid container spacing={4} mb={6}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: 4, 
            borderRadius: 6, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
            border: '1px solid #e0f2fe',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
            height: '100%'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Typography variant="overline" sx={{ fontWeight: '900', color: '#64748b', letterSpacing: '1.5px' }}>
                SPAM PROTECTION
              </Typography>
              <SecurityIcon sx={{ color: '#3b82f6', opacity: 0.8 }} />
            </Box>
            
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={6}>
                <Typography variant="h3" fontWeight="900" sx={{ color: '#1e293b' }}>{totalAttempts}</Typography>
                <Typography variant="body2" color="textSecondary" fontWeight="600">Analysis Attempts</Typography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <Typography variant="h4" fontWeight="800" sx={{ color: '#3b82f6' }}>
                  {totalBlocked}
                </Typography>
                <Typography variant="body2" color="textSecondary" fontWeight="600">Spam Prevented</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ 
            p: 4, 
            borderRadius: 6, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
            border: '1px solid #dcfce7',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
            height: '100%'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Typography variant="overline" sx={{ fontWeight: '900', color: '#64748b', letterSpacing: '1.5px' }}>
                LEAD INTEGRITY
              </Typography>
              <VerifiedUserIcon sx={{ color: '#10b981', opacity: 0.8 }} />
            </Box>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={6}>
                <Typography variant="h3" fontWeight="900" sx={{ color: '#064e3b' }}>{totalVerified}</Typography>
                <Typography variant="body2" color="textSecondary" fontWeight="600">Emails Verified</Typography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <Typography variant="h4" fontWeight="800" sx={{ color: '#10b981' }}>{totalDeliverable}</Typography>
                <Typography variant="body2" color="textSecondary" fontWeight="600">Real Deliverable</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {!settings.spam_shield_enabled ? (
        <Paper variant="outlined" style={{ padding: 40, borderRadius: 12, textAlign: 'center', backgroundColor: '#f9fafb' }}>
          <SecurityIcon style={{ fontSize: 64, color: '#9ca3af', marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>Spam Shield is currently disabled</Typography>
          <Typography variant="body2" color="textSecondary" style={{ maxWidth: 500, margin: '0 auto 24px' }}>
            To start protecting your site from bots and verifying lead quality, you first need to enable the master feature in your site settings.
          </Typography>
          <Button
            color="secondary"
            label="Enable in Site Settings"
            onClick={() => {
              try { window.analytics?.track('Spam Shield Enable Clicked', { site_id: site.id }); } catch (_) {}
              history.push(`/sites/${site.id}/settings/site`);
            }}
            size="large"
          />
        </Paper>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" style={{ padding: 24, borderRadius: 12, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Layer 1: Real-Time Spam Detection
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Analyzes submissions against 500M+ known malicious IPs and email patterns using advanced detection algorithms.
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box mt={2} mb={1}>
                      <Typography variant="body2" gutterBottom>
                        Sensitivity Threshold: <strong>{settings.spam_shield_threshold}</strong>
                      </Typography>
                      <Slider
                        value={settings.spam_shield_threshold}
                        onChange={handleSliderChange}
                        step={1}
                        marks={[
                          { value: 0, label: 'Strict' },
                          { value: 4, label: 'Default' },
                          { value: 6, label: 'Relaxed' },
                        ]}
                        min={0}
                        max={6}
                        color="secondary"
                        style={{ width: '90%', marginLeft: '5%' }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.spam_shield_auto_remove}
                          onChange={handleChange('spam_shield_auto_remove')}
                          color="secondary"
                        />
                      }
                      label="Automatically block and remove spam submissions"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" style={{ padding: 24, borderRadius: 12, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Layer 2: Deep Email Verification
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Verifies if the email address actually exists and is capable of receiving mail. Recommended for cleaning your list.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.spam_shield_verification_enabled}
                          onChange={handleChange('spam_shield_verification_enabled')}
                          color="secondary"
                        />
                      }
                      label={
                        <span>
                          Enable Deep Email Verification <small style={{ color: '#666', fontWeight: 'normal', marginLeft: '5px' }}>($0.01 per email)</small>
                        </span>
                      }
                    />
                    {settings.spam_shield_verification_enabled && !site.spam_shield_verification_enabled && (
                      <div style={{
                        marginTop: '8px',
                        marginBottom: '8px',
                        padding: '12px 16px',
                        backgroundColor: '#e0f2fe',
                        borderLeft: '4px solid #0284c7',
                        borderRadius: '4px',
                        color: '#0369a1',
                        fontSize: '12.5px',
                        lineHeight: '1.5',
                      }}>
                        <strong>Billing Info:</strong> Deep Email Verification checks if email addresses actually exist in real-time. Each verification is billed at <strong>$0.01</strong> and will be added to your monthly Stripe invoice.
                      </div>
                    )}
                    {!settings.spam_shield_verification_enabled && site.spam_shield_verification_enabled && (
                      <div style={{
                        marginTop: '8px',
                        marginBottom: '8px',
                        padding: '12px 16px',
                        backgroundColor: '#f3f4f6',
                        borderLeft: '4px solid #4b5563',
                        borderRadius: '4px',
                        color: '#374151',
                        fontSize: '12.5px',
                        lineHeight: '1.5',
                      }}>
                        <strong>Notice:</strong> Disabling Deep Email Verification will turn off real-time mailbox checking. You will only be billed for verifications processed up to this point in the current billing cycle.
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  type="submit"
                  loading={loading}
                  color="secondary"
                  label="Save Spam Shield Settings"
                  size="large"
                />
                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                  Billing: $15/mo base + $0.01 per email verified. Billed monthly.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default SpamShield;
